import AdSenseBanner from "@/components/AdSenseBanner";
import AdSenseServerCard from "@/components/AdSenseServerCard";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  fetchServerDetail,
  fetchBetterTeams,
  fetchTeamServers,
} from "@/lib/api";
import { toast } from "sonner";
import { sfx } from "@/lib/sounds";
import SectionHeader from "@/components/SectionHeader";
import ServerDrkCEco from "@/components/drkceco/ServerDrkCEco";
import EventCard from "@/components/EventCard";
import UserCard from "@/components/UserCard";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Users, Trophy, Sparkles, Copy, Check, Zap, ShieldCheck, Server as ServerIcon,
  Calendar, Image as ImageIcon, ShoppingBag, MessageSquare, ThumbsUp, BookOpen,
  Newspaper, HelpCircle, ChevronDown, ChevronUp, ExternalLink, ListChecks, Info,
} from "lucide-react";

function teamSlug(team) {
  if (team?.slug) return team.slug;

  return String(team?.name || "team")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function useCopy() {
  const [copied, setCopied] = useState(false);
  return {
    copied,
    copy: async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true); sfx.copy();
        toast.success("Copied");
        setTimeout(() => setCopied(false), 1500);
      } catch { /* ignore */ }
    },
  };
}

function FaqItem({ q, a, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl glass overflow-hidden" data-testid="server-faq-item">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <span className="font-semibold text-sm">{q}</span>
        {open ? <ChevronUp size={16} style={{ color }} /> : <ChevronDown size={16} className="text-white/50" />}
      </button>
      {open && a && <div className="px-4 pb-4 text-sm text-white/70 whitespace-pre-wrap">{a}</div>}
    </div>
  );
}

function Widget({ w, color }) {
  const cfg = w.config || {};
  if (w.type === "text") return (
    <div className="rounded-xl glass p-4" data-testid={`server-widget-text`}>
      {w.title && <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color }}>{w.title}</div>}
      <div className="text-sm text-white/80 whitespace-pre-wrap">{cfg.body || cfg.text || ""}</div>
    </div>
  );
  if (w.type === "link") return (
    <a href={cfg.url || "#"} target="_blank" rel="noreferrer" className="rounded-xl glass p-4 hover:border-[#FFBF00]/40 block" data-testid="server-widget-link">
      <div className="flex items-center gap-2 text-sm font-semibold"><ExternalLink size={14} style={{ color }} /> {w.title || cfg.url}</div>
      {cfg.description && <div className="mt-1 text-xs text-white/50">{cfg.description}</div>}
    </a>
  );
  if (w.type === "stat") return (
    <div className="rounded-xl glass p-4" data-testid="server-widget-stat">
      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>{w.title || "Stat"}</div>
      <div className="font-hero text-3xl mt-1">{cfg.value ?? "—"}</div>
      {cfg.subtitle && <div className="text-xs text-white/40 mt-1">{cfg.subtitle}</div>}
    </div>
  );
  if (w.type === "image") return (
    <a href={cfg.url || "#"} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden glass hover:border-[#FFBF00]/40" data-testid="server-widget-image">
      {cfg.src && <img src={cfg.src} alt="" className="w-full h-full object-cover" />}
      {w.title && <div className="p-3 text-sm font-semibold">{w.title}</div>}
    </a>
  );
  // Fallback: render title only
  return (
    <div className="rounded-xl glass p-4">
      <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>{w.type}</div>
      {w.title && <div className="text-sm font-semibold mt-1">{w.title}</div>}
    </div>
  );
}

export default function ServerDetail() {
  const { key } = useParams();

  const [teamsData, setTeamsData] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [data, setData] = useState(null);
  const ipCopy = useCopy();

  // Load the actual server details.
  // Load server details from cache immediately, then refresh silently.
  useEffect(() => {
    if (!key) {
      setData(null);
      return;
    }

    let cancelled = false;
    const cacheKey = `lostdc_server_detail_${key}`;

    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      try {
        const parsed = JSON.parse(cached);

        if (parsed?.data) {
          setData(parsed.data);
        }
      } catch {
        sessionStorage.removeItem(cacheKey);
      }
    }

    const loadServer = async () => {
      try {
        const result = await fetchServerDetail(key);

        if (!cancelled && result) {
          setData(result);

          sessionStorage.setItem(
            cacheKey,
            JSON.stringify({
              data: result,
              savedAt: Date.now(),
            })
          );
        }
      } catch (error) {
        console.error("Failed to load server details:", error);

        // Keep cached server data visible if the refresh fails.
        if (!cancelled && !cached) {
          setData(null);
        }
      }
    };

    loadServer();

    return () => {
      cancelled = true;
    };
  }, [key]);

  // Restore the exact server-page scroll position after returning from a team.
  useEffect(() => {
    if (!key || !data) return;

    const scrollKey = `lostdc_server_scroll_${key}`;
    const savedPosition = sessionStorage.getItem(scrollKey);

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    let cancelled = false;

    const restore = () => {
      if (cancelled || savedPosition === null) return;

      const target = Number(savedPosition);

      if (!Number.isFinite(target)) return;

      window.scrollTo({
        top: target,
        left: 0,
        behavior: "auto",
      });
    };

    const timers = [
      setTimeout(restore, 0),
      setTimeout(restore, 50),
      setTimeout(restore, 150),
      setTimeout(restore, 300),
      setTimeout(restore, 600),
      setTimeout(restore, 1000),
    ];

    let frame = 0;

    const restoreFrames = () => {
      if (cancelled || frame >= 8) return;

      restore();
      frame += 1;

      requestAnimationFrame(restoreFrames);
    };

    requestAnimationFrame(restoreFrames);

    const savePosition = () => {
      sessionStorage.setItem(
        scrollKey,
        String(Math.round(window.scrollY))
      );
    };

    window.addEventListener("scroll", savePosition, {
      passive: true,
    });

    return () => {
      cancelled = true;

      timers.forEach(clearTimeout);

      savePosition();

      window.removeEventListener(
        "scroll",
        savePosition
      );

      window.history.scrollRestoration =
        previousScrollRestoration;
    };
  }, [key, data]);

  // Load Teams independently so a Teams/API failure
  // never prevents the main server page from rendering.
  useEffect(() => {
    if (!key) return;

    let cancelled = false;

    const loadTeams = async () => {
      setTeamsLoading(true);

      try {
        const response = await fetchTeamServers();
        const servers = response?.servers || [];

        const normalizedKey = String(key).toLowerCase();

        const matchedServer =
          servers.find(
            (server) =>
              String(server.server_id || "").toLowerCase() === normalizedKey
          ) ||
          servers.find(
            (server) =>
              String(server.server_id || "")
                .toLowerCase()
                .startsWith(`${normalizedKey}-`)
          ) ||
          servers.find(
            (server) =>
              String(server.name || "")
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .includes(normalizedKey)
          );

        if (!matchedServer?.server_id) {
          if (!cancelled) setTeamsData(null);
          return;
        }

        const result = await fetchBetterTeams(matchedServer.server_id);

        if (!cancelled) {
          setTeamsData(result);
        }
      } catch {
        if (!cancelled) {
          setTeamsData(null);
        }
      } finally {
        if (!cancelled) {
          setTeamsLoading(false);
        }
      }
    };

    loadTeams();

    return () => {
      cancelled = true;
    };
  }, [key]);

  const {
    server: s,
    live = {},
    events = [],
    builds = [],
    creators = [],
    staff = [],
    announcements = [],
    leaderboard = {},
    community = null,
  } = data || {};

  if (!data || !s || !live) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-10 h-10 mx-auto mb-5 rounded-full border-2 border-white/10 border-t-[#FFBF00] animate-spin" />
          <div className="text-sm font-semibold text-white/80">
            Loading server...
          </div>
          <div className="text-xs text-white/35 mt-2">
            Connecting to LostDC network
          </div>
        </div>
      </div>
    );
  }


  const teams = teamsData?.teams || [];
  const teamsServer = teamsData?.server || null;
  const color = s.color || "#FFBF00";
  const banner = s.banner || s.image;
  const features = (s.features && s.features.length ? s.features : s.highlights) || [];
  const ipDisplay = s.port ? `${s.ip}:${s.port}` : s.ip;

  return (
    <div className="pb-24" data-testid={`page-server-${s.key}`}>
      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          {banner && <img src={banner} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0"
               style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.92)), linear-gradient(135deg, ${color}22, transparent 60%)` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-[11px] uppercase tracking-[0.3em]"
               style={{ borderColor: `${color}55` }} data-testid="server-status-pill">
            <span className={`w-2 h-2 rounded-full ${live.online ? "bg-green-500 pulse-dot" : "bg-red-500"}`} />
            <span style={{ color }}>{live.online ? "Server Online" : "Offline"}</span> · {s.status}
          </div>
          <h1 className="mt-6 font-hero text-6xl sm:text-7xl md:text-8xl leading-[0.9]" style={{ color }}>{s.name}</h1>
          {s.tagline && <p className="mt-3 text-lg sm:text-xl text-white/70 max-w-3xl">{s.tagline}</p>}
          {s.description && <p className="mt-3 text-sm text-white/60 max-w-3xl">{s.description}</p>}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={() => ipCopy.copy(ipDisplay)} data-testid="server-copy-ip"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-mono text-sm"
                    style={{ backgroundColor: color, color: "#000" }}>
              <span>{ipDisplay}</span>
              {ipCopy.copied ? <Check size={16} /> : <Copy size={16} />}
            </button>
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass" data-testid="server-online-count">
              <Users size={14} style={{ color }} />
              <span className="font-mono text-sm">{live.count} / {s.max_players} online</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-3 rounded-full glass">
              <Zap size={14} style={{ color }} />
              <span className="font-mono text-sm">Java · {s.version}</span>
            </div>
          </div>

          {/* Link row */}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest">
            {s.store_url && (
              <a href={s.store_url} target="_blank" rel="noreferrer" data-testid="server-link-store"
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#FFBF00]/40">
                <ShoppingBag size={12} style={{ color }} /> Store
              </a>
            )}
            {s.discord_url && (
              <a href={s.discord_url} target="_blank" rel="noreferrer" data-testid="server-link-discord"
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#7289DA]/40">
                <MessageSquare size={12} style={{ color: "#7289DA" }} /> Discord
              </a>
            )}
            {(s.vote_urls || []).map((v, i) => (
              <a key={`vote-${i}`} href={v} target="_blank" rel="noreferrer" data-testid={`server-link-vote-${i}`}
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-emerald-400/40">
                <ThumbsUp size={12} className="text-emerald-400" /> Vote {(s.vote_urls || []).length > 1 ? `#${i + 1}` : ""}
              </a>
            ))}
            {community && (
              <Link to={`/communities/${community.slug}`} data-testid="server-link-community"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full glass hover:border-[#FFBF00]/40">
                <Users size={12} style={{ color }} /> Discussion ({community.member_count})
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* DrkC Eco — server commands & guide */}
      <ServerDrkCEco serverKey={s.key || key} color={color} />

      {/* Announcements banner */}
      {announcements?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-10">
          <div className="rounded-2xl glass p-4 flex flex-wrap gap-4 items-center">
            <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>Latest</div>
            <div className="text-sm text-white/80">{announcements[0].title}</div>
          </div>
        </section>
      )}

      {/* Rich markdown body (About Section) */}
      {s.content_md && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-content-md">
          <SectionHeader eyebrow="About" title={`Inside ${s.name}`} />
          <div className="rounded-2xl glass p-8 sm:p-10 border border-white/10 shadow-2xl relative overflow-hidden backdrop-blur-xl">
            <div 
              className="absolute -right-20 -top-20 w-64 h-64 rounded-full blur-3xl opacity-10 pointer-events-none" 
              style={{ background: color }} 
            />
            <div className="relative z-10 prose prose-invert max-w-none text-white/85 text-base leading-relaxed [&>h1]:font-hero [&>h1]:text-3xl [&>h1]:mb-6 [&>h1]:tracking-wide [&>h2]:font-hero [&>h2]:text-2xl [&>h2]:mt-8 [&>h2]:mb-4 [&>h3]:font-semibold [&>h3]:text-xl [&>h3]:mt-6 [&>h3]:mb-3 [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:space-y-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:space-y-2 [&>blockquote]:border-l-4 [&>blockquote]:border-[#FFBF00] [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-white/70 [&>hr]:border-white/10 [&>hr]:my-6">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.content_md}</ReactMarkdown>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      {features.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Features" title={`What makes ${s.name} unique`} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="server-features">
            {features.map((h, i) => (
              <div key={`hl-${i}-${h}`} className="rounded-xl glass p-5">
                <Sparkles size={16} style={{ color }} className="mb-3" />
                <div className="font-semibold">{h}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Gallery */}
      {(s.gallery || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-gallery">
          <SectionHeader eyebrow="Gallery" title="Screenshots" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {s.gallery.map((src, i) => (
              <a key={`g-${i}`} href={src} target="_blank" rel="noreferrer" data-testid={`server-gallery-${i}`}
                 className="block aspect-video rounded-xl overflow-hidden glass group hover:border-[#FFBF00]/40">
                <img src={src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
              </a>
            ))}

            {/* Sponsored Ad */}
            <AdSenseServerCard />
          </div>
        </section>
      )}

      {/* Rules */}
      {(s.rules || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-rules">
          <SectionHeader eyebrow="Rules" title="Play Fair. Play Legendary." />
          <div className="rounded-2xl glass p-6">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={14} style={{ color }} />
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color }}>Server-specific rules</div>
            </div>
            <ol className="space-y-2 text-sm text-white/80 list-decimal list-inside">
              {s.rules.map((r, i) => <li key={`rule-${i}`}>{r}</li>)}
            </ol>
          </div>
        </section>
      )}

      {/* Live players */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
        <SectionHeader eyebrow="Live Now" title="Who's Playing" subtitle="Real-time roster on this server." />
        {live.players?.length === 0 ? (
          <div className="rounded-2xl glass p-8 text-center text-white/50 text-sm">No players online right now.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {live.players.map((p) => (
              <div key={p.username} className="flex items-center gap-2 px-3 py-2 rounded-full glass" data-testid={`live-player-${p.username}`}>
                <img src={p.avatar} alt="" className="w-6 h-6 rounded" />
                <span className="text-sm font-mono">{p.username}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Leaderboard */}
      {leaderboard?.entries?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Top Players" title="Server Leaderboard" />
          <div className="grid md:grid-cols-2 gap-3">
            {leaderboard.entries.slice(0, 8).map((e) => (
              <div key={e.rank} className="flex items-center gap-4 p-3 rounded-lg glass">
                <div className="font-hero text-2xl w-10" style={{ color }}>#{e.rank}</div>
                <img src={`https://mc-heads.net/avatar/${e.username}/64`} alt="" className="w-9 h-9 rounded" loading="lazy" />
                <div className="flex-1 font-semibold">{e.username}</div>
                <div className="font-mono" style={{ color }}>
                  {Number(e.value ?? 0).toLocaleString("en-IN")}
                  <span className="text-white/40 text-xs"> {e.unit || ""}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <Link to="/leaderboards" className="text-xs uppercase tracking-widest hover:underline" style={{ color }}>Full leaderboards →</Link>
          </div>
        </section>
      )}

      {/* TEAMS CHAMPIONSHIP */}
      {teams.length > 0 && (
        <section
          className="max-w-7xl mx-auto px-4 sm:px-6 mt-20"
          data-testid="server-teams"
        >
          {/* Header */}
          <div className="relative mb-10">
            <div
              className="absolute -left-10 -top-10 w-40 h-40 rounded-full blur-3xl opacity-20 pointer-events-none"
              style={{ background: color }}
            />

            <div className="relative">
              <div
                className="text-[10px] uppercase tracking-[0.4em] font-black"
                style={{ color }}
              >
                TEAMS · CHAMPIONSHIP
              </div>

              <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
                <div>
                  <h2 className="mt-2 font-hero text-4xl sm:text-5xl md:text-6xl">
                    Team Dominion
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm sm:text-base text-white/45">
                    The strongest teams on {teamsServer?.name || s.name},
                    ranked by their live Teams score.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <div className="px-4 py-3 rounded-2xl border border-white/10 bg-white/[0.025]">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-white/35">
                      Teams
                    </div>
                    <div className="text-xl font-black mt-1">
                      {teams.length}
                    </div>
                  </div>

                  <div className="px-4 py-3 rounded-2xl border border-white/10 bg-white/[0.025]">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-white/35">
                      Players
                    </div>
                    <div className="text-xl font-black mt-1">
                      {teams.reduce(
                        (sum, team) => sum + Number(team.member_count || 0),
                        0
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* TOP 3 PODIUM */}
          {teams.length >= 3 && (
            <div className="relative mb-14">
              <div
                className="absolute inset-x-10 top-10 h-72 rounded-full blur-3xl opacity-10 pointer-events-none"
                style={{ background: color }}
              />

              <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5 items-end">

                {/* SECOND */}
                {(() => {
                  const team = teams[1];
                  const teamName = team.display_name || team.name || "Unnamed Team";
                  const ownerName =
                    team.owner_username ||
                    (Array.isArray(team.owner_names) && team.owner_names.length
                      ? team.owner_names.join(", ")
                      : "Unknown");

                  return (
                    <Link
                      to={`/teams/${encodeURIComponent(teamsServer.server_id)}/${encodeURIComponent(teamSlug(team))}`}
                      className="group order-2 md:order-1"
                    >
                      <div className="relative min-h-[330px] md:min-h-[370px] rounded-[2rem] border border-white/15 bg-gradient-to-b from-white/[0.08] to-white/[0.025] overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:border-white/30 hover:shadow-[0_25px_80px_rgba(255,255,255,0.08)]">

                        <div className="absolute inset-x-0 top-0 h-1 bg-white/30" />

                        <div className="absolute -top-24 -right-24 w-52 h-52 rounded-full bg-white/10 blur-3xl" />

                        <div className="relative h-full p-6 flex flex-col items-center text-center">
                          <div className="text-5xl mb-2">🥈</div>

                          <div className="text-[10px] uppercase tracking-[0.35em] text-white/35 font-black">
                            2ND PLACE
                          </div>

                          <div className="mt-5 relative">
                            <div className="absolute inset-0 rounded-3xl bg-white/20 blur-xl opacity-50" />

                            {team.logo_url ? (
                              <img
                                src={team.logo_url}
                                alt=""
                                className="relative w-24 h-24 rounded-3xl object-cover border border-white/20 shadow-2xl"
                              />
                            ) : (
                              <div
                                className="relative w-24 h-24 rounded-3xl flex items-center justify-center text-4xl font-black border border-white/20"
                                style={{
                                  background: `${color}18`,
                                  color,
                                }}
                              >
                                {teamName.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>

                          <h3 className="mt-5 text-2xl font-black truncate max-w-full">
                            {teamName}
                          </h3>

                          {team.tag && (
                            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/35">
                              [{team.tag}]
                            </div>
                          )}

                          <div className="mt-5 w-full grid grid-cols-3 gap-2">
                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.score || 0).toLocaleString("en-IN")}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Score
                              </div>
                            </div>

                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.member_count || 0)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Members
                              </div>
                            </div>

                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.level || 1)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Level
                              </div>
                            </div>
                          </div>

                          <div className="mt-auto pt-5 text-xs text-white/40">
                            👑 {ownerName}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })()}

                {/* FIRST */}
                {(() => {
                  const team = teams[0];
                  const teamName = team.display_name || team.name || "Unnamed Team";
                  const ownerName =
                    team.owner_username ||
                    (Array.isArray(team.owner_names) && team.owner_names.length
                      ? team.owner_names.join(", ")
                      : "Unknown");

                  return (
                    <Link
                      to={`/teams/${encodeURIComponent(teamsServer.server_id)}/${encodeURIComponent(teamSlug(team))}`}
                      className="group order-1 md:order-2"
                    >
                      <div
                        className="relative min-h-[390px] md:min-h-[450px] rounded-[2.25rem] overflow-hidden transition-all duration-500 hover:-translate-y-4"
                        style={{
                          border: `1px solid ${color}66`,
                          background: `linear-gradient(145deg, ${color}18, rgba(255,255,255,0.035))`,
                          boxShadow: `0 25px 100px ${color}18`,
                        }}
                      >
                        <div
                          className="absolute inset-x-0 top-0 h-1.5"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
                          }}
                        />

                        <div
                          className="absolute -top-32 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full blur-3xl opacity-20"
                          style={{ background: color }}
                        />

                        <div className="relative h-full p-7 flex flex-col items-center text-center">

                          <div className="relative">
                            <div className="absolute -inset-4 rounded-full border border-[#FFBF00]/20 animate-pulse" />
                            <div className="absolute -inset-8 rounded-full border border-[#FFBF00]/10" />

                            <div className="text-7xl drop-shadow-2xl">
                              👑
                            </div>
                          </div>

                          <div
                            className="mt-3 text-[10px] uppercase tracking-[0.45em] font-black"
                            style={{ color }}
                          >
                            WORLD CHAMPION
                          </div>

                          <div className="mt-5 relative">
                            <div
                              className="absolute inset-0 rounded-[2rem] blur-2xl opacity-60"
                              style={{ background: color }}
                            />

                            {team.logo_url ? (
                              <img
                                src={team.logo_url}
                                alt=""
                                className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-[2rem] object-cover border-2 shadow-2xl"
                                style={{ borderColor: `${color}66` }}
                              />
                            ) : (
                              <div
                                className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-[2rem] flex items-center justify-center text-6xl font-black border-2 shadow-2xl"
                                style={{
                                  background: `${color}16`,
                                  color,
                                  borderColor: `${color}66`,
                                }}
                              >
                                {teamName.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>

                          <div className="mt-5 flex items-center gap-2">
                            <span
                              className="text-xs font-black uppercase tracking-[0.3em]"
                              style={{ color }}
                            >
                              #1
                            </span>
                            <span className="text-white/20">·</span>
                            <span className="text-xs text-white/40 uppercase tracking-widest">
                              TOP TEAM
                            </span>
                          </div>

                          <h3 className="mt-2 text-3xl sm:text-4xl font-black truncate max-w-full">
                            {teamName}
                          </h3>

                          {team.tag && (
                            <div
                              className="mt-1 text-[10px] uppercase tracking-[0.3em]"
                              style={{ color: `${color}99` }}
                            >
                              [{team.tag}]
                            </div>
                          )}

                          <div className="mt-6">
                            <div
                              className="text-4xl sm:text-5xl font-black"
                              style={{ color }}
                            >
                              {Number(team.score || 0).toLocaleString("en-IN")}
                            </div>
                            <div className="text-[9px] uppercase tracking-[0.35em] text-white/30 mt-1">
                              TEAM SCORE
                            </div>
                          </div>

                          <div className="mt-5 grid grid-cols-3 gap-2 w-full">
                            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                              <div className="font-black text-lg">
                                {Number(team.member_count || 0)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Members
                              </div>
                            </div>

                            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                              <div className="font-black text-lg">
                                {Number(team.level || 1)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Level
                              </div>
                            </div>

                            <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                              <div className="font-black text-lg">
                                {Number(team.admin_count || 0)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Admins
                              </div>
                            </div>
                          </div>

                          <div className="mt-auto pt-5 text-xs text-white/50">
                            👑 <span className="text-white/70">{ownerName}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })()}

                {/* THIRD */}
                {(() => {
                  const team = teams[2];
                  const teamName = team.display_name || team.name || "Unnamed Team";
                  const ownerName =
                    team.owner_username ||
                    (Array.isArray(team.owner_names) && team.owner_names.length
                      ? team.owner_names.join(", ")
                      : "Unknown");

                  return (
                    <Link
                      to={`/teams/${encodeURIComponent(teamsServer.server_id)}/${encodeURIComponent(teamSlug(team))}`}
                      className="group order-3"
                    >
                      <div className="relative min-h-[330px] md:min-h-[370px] rounded-[2rem] border border-orange-300/20 bg-gradient-to-b from-orange-300/[0.07] to-white/[0.02] overflow-hidden transition-all duration-500 hover:-translate-y-3 hover:border-orange-300/40 hover:shadow-[0_25px_80px_rgba(251,146,60,0.08)]">

                        <div className="absolute inset-x-0 top-0 h-1 bg-orange-300/30" />

                        <div className="relative h-full p-6 flex flex-col items-center text-center">
                          <div className="text-5xl mb-2">🥉</div>

                          <div className="text-[10px] uppercase tracking-[0.35em] text-orange-200/40 font-black">
                            3RD PLACE
                          </div>

                          <div className="mt-5">
                            {team.logo_url ? (
                              <img
                                src={team.logo_url}
                                alt=""
                                className="w-24 h-24 rounded-3xl object-cover border border-orange-200/20 shadow-2xl"
                              />
                            ) : (
                              <div
                                className="w-24 h-24 rounded-3xl flex items-center justify-center text-4xl font-black border border-orange-200/20"
                                style={{
                                  background: `${color}12`,
                                  color,
                                }}
                              >
                                {teamName.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>

                          <h3 className="mt-5 text-2xl font-black truncate max-w-full">
                            {teamName}
                          </h3>

                          {team.tag && (
                            <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/35">
                              [{team.tag}]
                            </div>
                          )}

                          <div className="mt-5 w-full grid grid-cols-3 gap-2">
                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.score || 0).toLocaleString("en-IN")}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Score
                              </div>
                            </div>

                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.member_count || 0)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Members
                              </div>
                            </div>

                            <div className="rounded-xl bg-black/20 p-3">
                              <div className="text-lg font-black">
                                {Number(team.level || 1)}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/30">
                                Level
                              </div>
                            </div>
                          </div>

                          <div className="mt-auto pt-5 text-xs text-white/40">
                            👑 {ownerName}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })()}
              </div>
            </div>
          )}

          {/* REMAINING TEAMS */}
          {teams.length > 3 && (
            <div className="mt-8">
              <div className="flex items-center gap-4 mb-5">
                <div className="h-px flex-1 bg-white/10" />
                <div className="text-[9px] uppercase tracking-[0.35em] text-white/30 font-black">
                  Championship Rankings
                </div>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {teams.slice(3).map((team, index) => {
                  const rank = Number(team.rank || index + 4);
                  const teamName =
                    team.display_name || team.name || "Unnamed Team";

                  const ownerName =
                    team.owner_username ||
                    (Array.isArray(team.owner_names) && team.owner_names.length
                      ? team.owner_names.join(", ")
                      : "Unknown");

                  return (
                    <Link
                      key={team.team_id}
                      to={`/teams/${encodeURIComponent(teamsServer.server_id)}/${encodeURIComponent(teamSlug(team))}`}
                      className="group"
                    >
                      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.025] transition-all duration-300 hover:-translate-y-1 hover:border-white/20 hover:bg-white/[0.045]">

                        <div
                          className="absolute left-0 top-0 bottom-0 w-1"
                          style={{ background: color }}
                        />

                        <div className="p-5">
                          <div className="flex items-center gap-4">

                            <div
                              className="w-14 h-14 shrink-0 rounded-2xl flex items-center justify-center border border-white/10 font-black text-lg"
                              style={{
                                background: `${color}0d`,
                                color,
                              }}
                            >
                              #{rank}
                            </div>

                            {team.logo_url ? (
                              <img
                                src={team.logo_url}
                                alt=""
                                className="w-14 h-14 rounded-2xl object-cover border border-white/10 shrink-0"
                                loading="lazy"
                              />
                            ) : (
                              <div
                                className="w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-black border border-white/10 shrink-0"
                                style={{
                                  background: `${color}12`,
                                  color,
                                }}
                              >
                                {teamName.charAt(0).toUpperCase()}
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-black text-lg truncate group-hover:text-[#FFBF00] transition-colors">
                                  {teamName}
                                </h3>

                                {team.tag && (
                                  <span className="text-[9px] shrink-0 px-2 py-1 rounded-full border border-white/10 text-white/35">
                                    [{team.tag}]
                                  </span>
                                )}
                              </div>

                              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/35">
                                <span>👑 {ownerName}</span>
                                <span>👥 {Number(team.member_count || 0)}</span>
                                <span>LVL {Number(team.level || 1)}</span>
                              </div>
                            </div>

                            <div className="text-right shrink-0">
                              <div
                                className="text-2xl font-black"
                                style={{ color }}
                              >
                                {Number(team.score || 0).toLocaleString("en-IN")}
                              </div>
                              <div className="text-[8px] uppercase tracking-widest text-white/25">
                                Score
                              </div>
                            </div>
                          </div>

                          <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap gap-2 text-[10px] text-white/35">
                            <span className="px-2.5 py-1 rounded-lg bg-white/5">
                              👑 {Number(team.owner_count || 0)} Owners
                            </span>
                            <span className="px-2.5 py-1 rounded-lg bg-white/5">
                              🛡 {Number(team.admin_count || 0)} Admins
                            </span>
                            <span className="px-2.5 py-1 rounded-lg bg-white/5">
                              💰 {Number(team.money || 0).toLocaleString("en-IN")}
                            </span>

                            <span
                              className="ml-auto uppercase tracking-widest"
                              style={{ color }}
                            >
                              View Team →
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {teams.length < 3 && (
            <div className="grid md:grid-cols-2 gap-4">
              {teams.map((team, index) => {
                const rank = Number(team.rank || index + 1);
                const teamName =
                  team.display_name || team.name || "Unnamed Team";

                return (
                  <Link
                    key={team.team_id}
                    to={`/teams/${encodeURIComponent(teamsServer.server_id)}/${encodeURIComponent(teamSlug(team))}`}
                    className="group"
                  >
                    <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-5 hover:border-white/20 transition-all">
                      <div className="flex items-center gap-4">
                        <div
                          className="text-xl font-black"
                          style={{ color }}
                        >
                          #{rank}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="font-black text-lg truncate">
                            {teamName}
                          </div>
                          <div className="text-xs text-white/35 mt-1">
                            {Number(team.member_count || 0)} members · Level{" "}
                            {Number(team.level || 1)}
                          </div>
                        </div>

                        <div
                          className="text-2xl font-black"
                          style={{ color }}
                        >
                          {Number(team.score || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* News */}
      {(s.news || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-news">
          <SectionHeader eyebrow="News" title="Latest updates" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {s.news.map((n, i) => (
              <article key={`n-${i}`} className="rounded-2xl glass overflow-hidden" data-testid={`server-news-${i}`}>
                {n.image && <img src={n.image} alt="" className="w-full aspect-video object-cover" />}
                <div className="p-4">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Newspaper size={11} /> {n.date}</div>
                  <div className="font-hero text-lg mt-1">{n.title}</div>
                  {n.body && <div className="mt-2 text-sm text-white/70 whitespace-pre-wrap line-clamp-4">{n.body}</div>}
                </div>
              </article>
            ))}

            {/* Sponsored Ad */}
            <AdSenseServerCard />
          </div>
        </section>
      )}

      {/* Changelog */}
      {(s.changelog || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-changelog">
          <SectionHeader eyebrow="Changelog" title="What's new & recent updates" />
          <div className="space-y-3">
            {s.changelog.map((c, i) => (
              <div key={`cl-${i}`} className="rounded-xl glass p-4" data-testid={`server-changelog-${i}`}>
                <div className="flex items-center gap-3">
                  <BookOpen size={14} style={{ color }} />
                  <div className="font-semibold">{c.title}</div>
                  <div className="ml-auto text-[10px] uppercase tracking-widest text-white/40">{c.date}</div>
                </div>
                {c.notes && Array.isArray(c.notes) && c.notes.length > 0 && (
                  <ul className="mt-2 pl-6 space-y-1 text-sm text-white/70 list-disc">
                    {c.notes.map((line, j) => <li key={`cl-${i}-${j}`}>{line}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {(s.faq || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-faq">
          <SectionHeader eyebrow="FAQ" title="Common questions" />
          <div className="grid md:grid-cols-2 gap-3">
            {s.faq.map((f, i) => <FaqItem key={`f-${i}`} q={f.q || f.question} a={f.a || f.answer} color={color} />)}
          </div>
        </section>
      )}

      {/* Widgets (admin-defined custom content) */}
      {(s.widgets || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14" data-testid="server-widgets">
          <SectionHeader eyebrow="More" title="Server widgets" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {s.widgets.map((w, i) => <Widget key={`w-${i}`} w={w} color={color} />)}
          </div>
        </section>
      )}

      {/* Related builds */}
      {builds?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Community" title="Builds on this server" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {builds.map((b) => (
              <Link key={b.id} to="/builds" className="rounded-xl overflow-hidden border border-white/10 group hover:border-[#FFBF00]/40">
                {(b.images?.[0] || b.image) && <img src={b.images?.[0] || b.image} alt="" className="w-full h-32 object-cover group-hover:scale-105 transition-transform" />}
                <div className="p-3">
                  <div className="text-sm font-semibold line-clamp-1">{b.title}</div>
                  <div className="text-[10px] text-white/40 uppercase tracking-widest">{b.category}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured creators */}
      {creators?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Creators" title="Creators who love this server" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {creators.map((c) => <UserCard key={c.id} user={c} />)}
          </div>
        </section>
      )}

      {/* Events */}
      {events?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Events" title="Coming up on this server" />
          <div className="grid md:grid-cols-2 gap-6">
            {events.map((e, i) => <EventCard key={e.id} event={e} index={i} />)}
          </div>
        </section>
      )}

      {/* Staff */}
      {staff?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="Team" title="Server Staff" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {staff.link || staff.map((m) => (
              <div key={m.id || m.name} className="rounded-xl glass p-4 flex items-center gap-3">
                {m.avatar && <img src={m.avatar} alt="" className="w-12 h-12 rounded-lg" />}
                <div>
                  <div className="font-semibold">{m.name}</div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color }}>{m.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* How to join */}
      {s.connection_steps?.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-14">
          <SectionHeader eyebrow="How to Join" title={`Connect to ${s.name}`} />
          <ol className="rounded-2xl glass p-6 space-y-3 text-sm text-white/80 list-decimal list-inside" data-testid="server-connection-steps">
            {s.connection_steps.map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </section>
      )}

      {/* Bottom AdSense Banner — same as Home page */}
      <div className="mt-14 max-w-7xl mx-auto px-4 sm:px-6">
        <AdSenseBanner />
      </div>
    </div>
  );
}